/**
 * Service for managing comments and quick phrases
 * Handles set and workout comments with predefined Russian phrases
 * Implements requirements 9.1, 9.2, 9.3, 12.2, 12.5
 */
export class CommentService {
  /**
   * Predefined quick phrases in Russian for sets
   * Requirements: 9.2, 12.2, 12.5
   */
  private static readonly SET_QUICK_PHRASES = [
    'Легко',
    'В запасе',
    'Тяжело',
    'До отказа',
    'Хорошая техника',
    'Плохая техника',
    'Быстро',
    'Медленно',
    'Полная амплитуда',
    'Частичные повторы',
    'С паузой',
    'Взрывной стиль',
    'Суперсет',
    'Дропсет',
    'Негативы',
    'Читинг'
  ];

  /**
   * Predefined quick phrases in Russian for workouts
   * Requirements: 9.2, 12.2, 12.5
   */
  private static readonly WORKOUT_QUICK_PHRASES = [
    'Отличная тренировка!',
    'Хорошая тренировка',
    'Тяжёлая тренировка',
    'Лёгкая тренировка',
    'Много энергии',
    'Мало энергии',
    'Хорошее настроение',
    'Плохое самочувствие',
    'Новый личный рекорд!',
    'Прогресс в весах',
    'Улучшение техники',
    'Нужно больше отдыха',
    'Отличная мотивация',
    'Сложно сосредоточиться',
    'Зал был переполнен',
    'Зал был пустой',
    'Новое упражнение',
    'Вернулся после перерыва',
    'Травма беспокоит',
    'Чувствую себя сильнее'
  ];

  /**
   * Get quick phrases for sets
   * Requirements: 9.2, 12.2
   */
  getSetQuickPhrases(): string[] {
    return [...CommentService.SET_QUICK_PHRASES];
  }

  /**
   * Get quick phrases for workouts
   * Requirements: 9.2, 12.2
   */
  getWorkoutQuickPhrases(): string[] {
    return [...CommentService.WORKOUT_QUICK_PHRASES];
  }

  /**
   * Validate comment text for Cyrillic characters and length
   * Requirements: 9.3, 12.2, 12.5
   */
  validateComment(comment: string): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    
    // Check length
    if (comment.length > 500) {
      errors.push('Комментарий слишком длинный (максимум 500 символов)');
    }
    
    // Check for empty comment (after trimming)
    if (comment.trim().length === 0) {
      errors.push('Комментарий не может быть пустым');
    }
    
    // Check for potentially harmful content (basic validation)
    const harmfulPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i
    ];
    
    for (const pattern of harmfulPatterns) {
      if (pattern.test(comment)) {
        errors.push('Комментарий содержит недопустимые символы');
        break;
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Format comment for display
   * Requirements: 9.4, 12.1, 12.2
   */
  formatComment(comment: string): string {
    if (!comment) return '';
    
    // Trim whitespace
    let formatted = comment.trim();
    
    // Capitalize first letter if it's Cyrillic
    if (formatted.length > 0) {
      const firstChar = formatted.charAt(0);
      const upperFirst = firstChar.toUpperCase();
      formatted = upperFirst + formatted.slice(1);
    }
    
    // Ensure proper punctuation
    if (formatted.length > 0 && !formatted.match(/[.!?]$/)) {
      // Don't add punctuation to single words or short phrases
      if (formatted.length > 10 && formatted.includes(' ')) {
        formatted += '.';
      }
    }
    
    return formatted;
  }

  /**
   * Search quick phrases by query
   * Requirements: 9.3
   */
  searchQuickPhrases(query: string, type: 'set' | 'workout' | 'exercise' = 'set'): string[] {
    if (!query || query.trim().length === 0) {
      return type === 'set' || type === 'exercise' ? this.getSetQuickPhrases() : this.getWorkoutQuickPhrases();
    }
    
    const phrases = type === 'set' || type === 'exercise' ? CommentService.SET_QUICK_PHRASES : CommentService.WORKOUT_QUICK_PHRASES;
    const searchQuery = query.toLowerCase().trim();
    
    return phrases.filter(phrase => 
      phrase.toLowerCase().includes(searchQuery)
    );
  }

  /**
   * Get comment statistics
   * Requirements: 9.1
   */
  getCommentStats(comments: string[]): {
    total: number;
    averageLength: number;
    mostCommon: string[];
    hasRussianText: boolean;
  } {
    if (comments.length === 0) {
      return {
        total: 0,
        averageLength: 0,
        mostCommon: [],
        hasRussianText: false
      };
    }

    // Calculate average length
    const totalLength = comments.reduce((sum, comment) => sum + comment.length, 0);
    const averageLength = Math.round(totalLength / comments.length);

    // Find most common comments
    const commentCounts = new Map<string, number>();
    comments.forEach(comment => {
      const trimmed = comment.trim();
      if (trimmed) {
        commentCounts.set(trimmed, (commentCounts.get(trimmed) || 0) + 1);
      }
    });

    const mostCommon = Array.from(commentCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([comment]) => comment);

    // Check for Russian text
    const cyrillicPattern = /[а-яё]/i;
    const hasRussianText = comments.some(comment => cyrillicPattern.test(comment));

    return {
      total: comments.length,
      averageLength,
      mostCommon,
      hasRussianText
    };
  }

  /**
   * Generate comment suggestions based on context
   * Requirements: 9.2, 9.3
   */
  generateSuggestions(context: {
    exerciseType?: string;
    weight?: number;
    reps?: number;
    previousComments?: string[];
    workoutDuration?: number;
  }): string[] {
    const suggestions: string[] = [];

    // Exercise-specific suggestions
    if (context.exerciseType) {
      const exerciseType = context.exerciseType.toLowerCase();
      
      if (exerciseType.includes('жим') || exerciseType.includes('press')) {
        suggestions.push('Хорошая техника', 'Полная амплитуда', 'Контролируемо');
      }
      
      if (exerciseType.includes('тяга') || exerciseType.includes('pull')) {
        suggestions.push('Сводим лопатки', 'Чувствую спину', 'Без читинга');
      }
      
      if (exerciseType.includes('присед') || exerciseType.includes('squat')) {
        suggestions.push('До параллели', 'Колени не заваливаются', 'Хорошая глубина');
      }
    }

    // Weight and reps based suggestions
    if (context.weight && context.reps) {
      if (context.reps >= 12) {
        suggestions.push('Много повторов', 'На выносливость', 'Жжение в мышцах');
      } else if (context.reps <= 5) {
        suggestions.push('Силовая работа', 'Тяжёлый вес', 'Максимальное усилие');
      }
    }

    // Previous comments analysis
    if (context.previousComments && context.previousComments.length > 0) {
      const lastComment = context.previousComments[context.previousComments.length - 1];
      if (lastComment.includes('тяжело') || lastComment.includes('сложно')) {
        suggestions.push('Нужно снизить вес', 'Больше отдыха', 'Проверить технику');
      } else if (lastComment.includes('легко') || lastComment.includes('запас')) {
        suggestions.push('Можно увеличить вес', 'Добавить повторы', 'Прогресс!');
      }
    }

    // Workout duration suggestions
    if (context.workoutDuration) {
      if (context.workoutDuration > 90) {
        suggestions.push('Долгая тренировка', 'Много объёма', 'Устал');
      } else if (context.workoutDuration < 30) {
        suggestions.push('Быстрая тренировка', 'Интенсивно', 'Эффективно');
      }
    }

    // Add some general suggestions
    suggestions.push(...CommentService.SET_QUICK_PHRASES.slice(0, 3));

    // Remove duplicates and limit to 8 suggestions
    return [...new Set(suggestions)].slice(0, 8);
  }

  /**
   * Check if comment contains Russian text
   * Requirements: 12.2, 12.5
   */
  isRussianText(text: string): boolean {
    const cyrillicPattern = /[а-яё]/i;
    return cyrillicPattern.test(text);
  }

  /**
   * Transliterate Russian text to Latin (basic)
   * Requirements: 12.5
   */
  transliterate(text: string): string {
    const transliterationMap: { [key: string]: string } = {
      'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
      'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
      'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
      'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
      'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya'
    };

    return text.toLowerCase().split('').map(char => {
      return transliterationMap[char] || char;
    }).join('');
  }
}

// Export singleton instance
export const commentService = new CommentService();